import { useCallback, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { parsePage, parsePageSize } from '@/lib/pagination';

export interface TableQueryState {
  page: number;
  pageSize: number;
  q: string;
  sort: string;                 // e.g. 'displayName' or '-createdAt'
  filters: Record<string, string>;
}

const RESERVED = new Set(['page', 'pageSize', 'q', 'sort']);

/**
 * Syncs table view state (page, size, search, sort, arbitrary filters) with the
 * URL query string — so a filtered/sorted list is shareable, reload-safe and
 * back-button correct. Changing anything but the page resets to page 1.
 */
export function useTableQueryState(filterKeys: string[] = []): {
  state: TableQueryState;
  setPage: (page: number) => void;
  setPageSize: (size: number) => void;
  setSearch: (q: string) => void;
  toggleSort: (field: string) => void;
  setFilter: (key: string, value: string) => void;
  reset: () => void;
} {
  const [params, setParams] = useSearchParams();

  const state = useMemo<TableQueryState>(() => {
    const filters: Record<string, string> = {};
    for (const key of filterKeys) {
      const v = params.get(key);
      if (v) filters[key] = v;
    }
    return {
      page: parsePage(params.get('page')),
      pageSize: parsePageSize(params.get('pageSize')),
      q: params.get('q') ?? '',
      sort: params.get('sort') ?? '',
      filters,
    };
  }, [params, filterKeys]);

  const write = useCallback(
    (next: Record<string, string | number | null>, resetPage = true) => {
      const merged = new URLSearchParams(params);
      for (const [k, v] of Object.entries(next)) {
        if (v === null || v === '') merged.delete(k);
        else merged.set(k, String(v));
      }
      if (resetPage && !('page' in next)) merged.set('page', '1');
      setParams(merged, { replace: true });
    },
    [params, setParams],
  );

  // Canonicalize a malformed page/pageSize in the URL (e.g. page=0, -3, abc,
  // 2.5) so the address bar always matches what the table shows. Out-of-range
  // pages are clamped by <Pagination> once the total is known.
  useEffect(() => {
    const rawPage = params.get('page');
    const rawSize = params.get('pageSize');
    const fix: Record<string, string> = {};
    if (rawPage !== null && rawPage !== String(state.page)) fix.page = String(state.page);
    if (rawSize !== null && rawSize !== String(state.pageSize)) fix.pageSize = String(state.pageSize);
    if (Object.keys(fix).length > 0) write(fix, false);
  }, [params, state.page, state.pageSize, write]);

  const setPage = useCallback((page: number) => write({ page: parsePage(page) }, false), [write]);
  const setPageSize = useCallback((size: number) => write({ pageSize: size }), [write]);
  const setSearch = useCallback((q: string) => write({ q: q || null }), [write]);
  const setFilter = useCallback((key: string, value: string) => write({ [key]: value || null }), [write]);

  /** Cycle sort: none -> asc -> desc -> none, encoded as '', 'field', '-field'. */
  const toggleSort = useCallback(
    (field: string) => {
      const current = params.get('sort') ?? '';
      let next: string | null = field;
      if (current === field) next = `-${field}`;
      else if (current === `-${field}`) next = null;
      write({ sort: next });
    },
    [params, write],
  );

  const reset = useCallback(() => {
    const cleared = new URLSearchParams();
    setParams(cleared, { replace: true });
  }, [setParams]);

  return { state, setPage, setPageSize, setSearch, toggleSort, setFilter, reset };
}

export function isReservedParam(key: string): boolean {
  return RESERVED.has(key);
}
